/**
 * Keeps `runningTurnsStore` honest for threads nobody has on screen.
 *
 * A chat that is mounted on a thread learns of its turn ending from the
 * realtime stream and clears it; a thread the person navigated away from has
 * no chat and no stream claim (claiming one per background turn would
 * reconnect the shared event stream on every start and finish). So those are
 * polled: the thread payload's `active_turn` says whether the tracked turn is
 * still the one running. Any `thread.updated` that does reach this tab (the
 * thread's scope is claimed by another view) is applied at once.
 *
 * Renders nothing. Mounted once, inside `RealtimeProvider`.
 */
import { useEffect, type FC } from "react";

import { workflowDisplayText } from "@/features/caseIde/workflows/catalog";
import { useRealtimeEvent } from "@/lib/realtime/RealtimeProvider";

import { AgentAPI, type AgentThread } from "../api/agentApi";
import {
  runningTurnsStore,
  selectOffscreenTurns,
  useRunningTurnsStore,
  type RunningTurn
} from "../runningTurnsStore";

export const OFFSCREEN_POLL_MS = 10_000;

/** Whether `thread`'s lock is still the tracked turn `turnId`. */
export const stillRunning = (
  thread: Pick<AgentThread, "active_turn">,
  turnId: string
): boolean => !!thread.active_turn && thread.active_turn.turn_id === turnId;

/** Carry the lock's latest progress label onto the tracked turn. */
const refreshStatus = (turn: RunningTurn, thread: AgentThread): void => {
  const title = thread.title || turn.title;
  runningTurnsStore.track({
    ...turn,
    title: title ? workflowDisplayText(title) : null,
    status: thread.active_turn?.status ?? null,
    statusAt: thread.active_turn?.status_at ?? null
  });
};

export const RunningTurnsWatcher: FC<{ pollMs?: number }> = ({
  pollMs = OFFSCREEN_POLL_MS
}) => {
  // The identity that matters for the poll: which turns are off screen. A
  // string, so a store change that leaves that set alone re-renders nothing.
  const offscreenKey = useRunningTurnsStore((s) =>
    selectOffscreenTurns(s)
      .map((t) => `${t.threadId}:${t.turnId}`)
      .join(",")
  );

  useRealtimeEvent<AgentThread>("thread.updated", (thread) => {
    if (!thread?.id) return;
    const tracked = runningTurnsStore
      .turns()
      .find((t) => t.threadId === thread.id);
    if (!tracked) return;
    if (!stillRunning(thread, tracked.turnId))
      runningTurnsStore.untrack(thread.id);
    else refreshStatus(tracked, thread);
  });
  useRealtimeEvent<{ id?: string; thread_id?: string }>(
    "thread.deleted",
    (data) => {
      const id = data?.id ?? data?.thread_id;
      if (id) runningTurnsStore.untrack(id);
    }
  );

  useEffect(() => {
    if (!offscreenKey) return undefined;
    let cancelled = false;
    const check = () => {
      const turns = selectOffscreenTurns(useRunningTurnsStore.getState());
      void Promise.all(
        turns.map((turn) =>
          AgentAPI.getThread(turn.scope, turn.threadId)
            .then(({ thread }) => {
              if (cancelled) return;
              if (!stillRunning(thread, turn.turnId))
                runningTurnsStore.untrack(turn.threadId);
              else refreshStatus(turn, thread);
            })
            .catch(() => {
              // Gone (deleted, access lost): nothing left to watch.
              if (!cancelled) runningTurnsStore.untrack(turn.threadId);
            })
        )
      );
    };
    const timer = window.setInterval(check, pollMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [offscreenKey, pollMs]);

  return null;
};
